import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'error';
}
interface ToastApi {
  push: (text: string, kind?: Toast['kind']) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setItems((s) => s.filter((t) => t.id !== id)), []);
  const push = useCallback(
    (text: string, kind: Toast['kind'] = 'info') => {
      const id = Date.now() + Math.random();
      setItems((s) => [...s, { id, text, kind }]);
      // UI test 01/10/2026: lỗi (thường dài, từ máy chủ) giữ 10 s + nút đóng — 4 s không kịp đọc (WCAG 2.2.1); thông tin giữ 4 s
      setTimeout(() => dismiss(id), kind === 'error' ? 10_000 : 4000);
    },
    [dismiss],
  );
  const api = useMemo(() => ({ push }), [push]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="kx-toasts" aria-live="polite">
        {items.map((t) => (
          <div
            key={t.id}
            className={`kx-toast ${t.kind === 'error' ? 'error' : ''}`}
            role={t.kind === 'error' ? 'alert' : undefined}
          >
            <span>{t.text}</span>
            {t.kind === 'error' ? (
              <button type="button" className="kx-toast-x" aria-label="Đóng thông báo" onClick={() => dismiss(t.id)}>
                ✕
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useToast phải nằm trong <ToastProvider>');
  return api;
}
